/**
 * AuthProvider — Real Cognito authentication using USER_PASSWORD_AUTH flow.
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import config from '../config/app.config';

export interface User {
  userId: string;
  email: string;
  name: string;
  role: string;
  tenantId: string;
  idToken: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  completeNewPassword: (newPassword: string) => Promise<void>;
  logout: () => void;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Thrown by login() when the account must set a new password on first login
 * (admin-created users start in FORCE_CHANGE_PASSWORD). The LoginPage catches
 * this and shows the set-new-password step, then calls completeNewPassword().
 */
export class NewPasswordRequiredError extends Error {
  code = 'NEW_PASSWORD_REQUIRED' as const;
  session: string;
  username: string;
  constructor(session: string, username: string) {
    super('New password required');
    this.name = 'NewPasswordRequiredError';
    this.session = session;
    this.username = username;
  }
}

// Highest-priority group wins when a user belongs to several. Standard kit
// role set, most privileged first.
const ROLE_PRIORITY = ['superadmin', 'admin', 'author', 'reader'];

function roleFromGroups(groups: unknown): string {
  const g = Array.isArray(groups) ? (groups as string[]) : [];
  for (const role of ROLE_PRIORITY) {
    if (g.includes(role)) return role;
  }
  return 'reader';
}

/** Returns true if the JWT has a valid, non-expired `exp` claim. */
function isTokenValid(idToken: string | undefined): boolean {
  if (!idToken) return false;
  try {
    const payload = JSON.parse(atob(idToken.split('.')[1]));
    if (!payload.exp) return false;
    // exp is seconds since epoch; add a small skew buffer.
    return payload.exp * 1000 > Date.now() + 5000;
  } catch {
    return false;
  }
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for stored session on mount — but only trust it if the token is
  // still valid (not expired). An expired token would otherwise make the user
  // "authenticated", land on /, then 401 and bounce to /login: a flashing loop.
  useEffect(() => {
    const stored = localStorage.getItem('ppcti_user');
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as User;
        if (isTokenValid(parsed.idToken)) {
          setUser(parsed);
        } else {
          // Expired/invalid — clear it so we show login cleanly.
          localStorage.removeItem('ppcti_user');
          localStorage.removeItem('ppcti_id_token');
          localStorage.removeItem('ppcti_access_token');
        }
      } catch {
        localStorage.removeItem('ppcti_user');
      }
    }
    setIsLoading(false);
  }, []);

  const isAuthenticated = user !== null;

  // Holds the Cognito challenge session + username between login() (which
  // discovers NEW_PASSWORD_REQUIRED) and completeNewPassword().
  const pendingRef = React.useRef<{ session: string; username: string } | null>(null);

  const cognitoCall = useCallback(async (target: string, body: Record<string, unknown>) => {
    const res = await fetch(`https://cognito-idp.${config.awsRegion}.amazonaws.com/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': `AWSCognitoIdentityProviderService.${target}`,
      },
      body: JSON.stringify(body),
    });
    return res.json();
  }, []);

  // Turn a Cognito AuthenticationResult into our User + persist it.
  const storeAuthResult = useCallback((authResult: Record<string, unknown>, fallbackName: string) => {
    const idToken = authResult?.IdToken as string | undefined;
    const accessToken = (authResult?.AccessToken as string) || '';
    if (!idToken) throw new Error('No token received from Cognito');
    const payload = JSON.parse(atob(idToken.split('.')[1]));
    const loggedInUser: User = {
      userId: payload.sub || fallbackName,
      email: payload.email || fallbackName,
      name: payload.name || fallbackName,
      role: roleFromGroups(payload['cognito:groups']),
      tenantId: 'tenant-demo-001',
      idToken,
    };
    setUser(loggedInUser);
    localStorage.setItem('ppcti_user', JSON.stringify(loggedInUser));
    localStorage.setItem('ppcti_id_token', idToken);
    localStorage.setItem('ppcti_access_token', accessToken);
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    setError(null);
    try {
      const data = await cognitoCall('InitiateAuth', {
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: config.cognitoClientId,
        AuthParameters: { USERNAME: username, PASSWORD: password },
      });

      if (data.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
        // First login for an admin-created user — remember the challenge so
        // completeNewPassword() can respond, and signal the UI.
        pendingRef.current = { session: data.Session, username };
        throw new NewPasswordRequiredError(data.Session, username);
      }

      if (data.__type || data.message) {
        throw new Error(data.message || 'Authentication failed');
      }

      storeAuthResult(data.AuthenticationResult, username);
    } catch (err: unknown) {
      if (err instanceof NewPasswordRequiredError) throw err; // not an error state
      const msg = err instanceof Error ? err.message : 'Login failed';
      setError(msg);
      throw err;
    }
  }, [cognitoCall, storeAuthResult]);

  const completeNewPassword = useCallback(async (newPassword: string) => {
    setError(null);
    const pending = pendingRef.current;
    if (!pending) {
      throw new Error('No pending password change. Please sign in again.');
    }
    try {
      const data = await cognitoCall('RespondToAuthChallenge', {
        ChallengeName: 'NEW_PASSWORD_REQUIRED',
        ClientId: config.cognitoClientId,
        Session: pending.session,
        ChallengeResponses: {
          USERNAME: pending.username,
          NEW_PASSWORD: newPassword,
        },
      });
      if (data.__type || data.message) {
        throw new Error(data.message || 'Could not set new password');
      }
      storeAuthResult(data.AuthenticationResult, pending.username);
      pendingRef.current = null;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Could not set new password';
      setError(msg);
      throw err;
    }
  }, [cognitoCall, storeAuthResult]);

  const logout = useCallback(() => {
    setUser(null);
    setError(null);
    localStorage.removeItem('ppcti_user');
    localStorage.removeItem('ppcti_id_token');
    localStorage.removeItem('ppcti_access_token');
  }, []);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isLoading, login, completeNewPassword, logout, error }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
